// To parse this data:
//
//   import { Convert, ProductAndSupplierMapModel } from "./file";
//
//   const productAndSupplierMapModel = Convert.toProductAndSupplierMapModel(json);
//
// These functions will throw an error if the JSON doesn't
// match the expected interface, even if the JSON is valid.

export interface ProductAndSupplierMapModel {
    status: string;
    data: Data;
}

export interface Data {
    productDenomination: ProductDenomination[];
    supplierDetails: SupplierDetail[];
}

export interface ProductDenomination {
    _id: string;
    productId: string;
    productsku: string;
    productDenominationId: string;
    productDenominationsku: string;
    supplierId: string;
    subSupplierId: string;
    supplierName: string;
    supplierCategory: string;
    executionType?: string;
    supplierProviderName: string;
    supplierProviderCode: string;
    isCustomMapping: boolean;
    supplerProductDenominationsku: string;
    supplierDiscountType: string;
    supplierDiscount: number;
    cashbackType: string;
    cashback: number;
    quantity: number;
    markupPrice: number;
    denomination: 0;
    cost_price_at_sale: number;
    excutionLevel: number;
    isActive: boolean;
    isDelete: boolean;
    createdAt: Date;
    updatedAt: Date;
    supplierFileId:string;
    __v: number;
    supplerProductDenominationAmt?: SupplerProductDenominationAmtModel;
    deactivatedBySync?: boolean;
    createdBy?: string;
    updatedBy?: string;
}

export interface SupplerProductDenominationAmtModel {
    value: number;
    currency: string;
    priceType: 'fixed' | 'range';
    min?: number;
    max?: number;
}

export interface SupplierDetail {
    supplierName: string;
    supplierCategory: any[];
    supplierProductDenominationsku?: string | null;
    supplierId: string;
    subSupplierId?: string;
    subSupplierName?: string;
}

// Add this function before or after the transform function
function transformExecutionType(val: any): string {
    // Convert undefined or null to empty string
    if (val === undefined || val === null) {
        return "";
    }
    return val;
}

// Converts JSON strings to/from your types
// and asserts the results of JSON.parse at runtime
// export class Convert {
//     public static toProductAndSupplierMapModel(json: string): ProductAndSupplierMapModel {
//         return cast(JSON.parse(json), r("ProductAndSupplierMapModel"));
//     }

//     public static productAndSupplierMapModelToJson(value: ProductAndSupplierMapModel): string {
//         return JSON.stringify(uncast(value, r("ProductAndSupplierMapModel")), null, 2);
//     }
// }

export class Convert {
    public static toProductAndSupplierMapModel(json: string): ProductAndSupplierMapModel {
        try {

            const parsed: any = cast(JSON.parse(json), r("ProductAndSupplierMapModel"));
            // Fix undefined executionType values
            if (parsed.data && parsed.data.productDenomination) {
                parsed.data.productDenomination.forEach((item: any) => {
                    // Fix executionType issue
                    if (item.executionType === undefined || item.executionType === null) {
                        item.executionType = "";
                    }

                    // Convert Date objects to ISO strings for Redux
                    if (item.createdAt instanceof Date) {
                        (item as any).createdAt = item.createdAt.toISOString();
                    }
                    if (item.updatedAt instanceof Date) {
                        (item as any).updatedAt = item.updatedAt.toISOString();
                    }
                });
            }

            return parsed;
        } catch (error) {
            throw error;
        }
    }

    public static productAndSupplierMapModelToJson(value: ProductAndSupplierMapModel): string {
        return JSON.stringify(uncast(value, r("ProductAndSupplierMapModel")), null, 2);
    }
}



function invalidValue(typ: any, val: any, key: any, parent: any = ''): never {
    const prettyTyp = prettyTypeName(typ);
    const parentText = parent ? ` on ${parent}` : '';
    const keyText = key ? ` for key "${key}"` : '';
    throw Error(`Invalid value${keyText}${parentText}. Expected ${prettyTyp} but got ${JSON.stringify(val)}`);
}

function prettyTypeName(typ: any): string {
    if (Array.isArray(typ)) {
        if (typ.length === 2 && typ[0] === undefined) {
            return `an optional ${prettyTypeName(typ[1])}`;
        } else {
            return `one of [${typ.map(a => { return prettyTypeName(a); }).join(", ")}]`;
        }
    } else if (typeof typ === "object" && typ.literal !== undefined) {
        return typ.literal;
    } else {
        return typeof typ;
    }
}

function jsonToJSProps(typ: any): any {
    if (typ.jsonToJS === undefined) {
        const map: any = {};
        typ.props.forEach((p: any) => map[p.json] = { key: p.js, typ: p.typ });
        typ.jsonToJS = map;
    }
    return typ.jsonToJS;
}

function jsToJSONProps(typ: any): any {
    if (typ.jsToJSON === undefined) {
        const map: any = {};
        typ.props.forEach((p: any) => map[p.js] = { key: p.json, typ: p.typ });
        typ.jsToJSON = map;
    }
    return typ.jsToJSON;
}

function transform(val: any, typ: any, getProps: any, key: any = '', parent: any = ''): any {
    function transformPrimitive(typ: string, val: any): any {
        if (typeof typ === typeof val) return val;
        return invalidValue(typ, val, key, parent);
    }

    function transformUnion(typs: any[], val: any): any {
        // val must validate against one typ in typs
        const l = typs.length;
        for (let i = 0; i < l; i++) {
            const typ = typs[i];
            try {
                return transform(val, typ, getProps);
            } catch (_) { }
        }
        return invalidValue(typs, val, key, parent);
    }

    function transformEnum(cases: string[], val: any): any {
        if (cases.indexOf(val) !== -1) return val;
        return invalidValue(cases.map(a => { return l(a); }), val, key, parent);
    }

    function transformArray(typ: any, val: any): any {
        // val must be an array with no invalid elements
        if (!Array.isArray(val)) return invalidValue(l("array"), val, key, parent);
        return val.map(el => transform(el, typ, getProps));
    }

    function transformDate(val: any): any {
        if (val === null) {
            return null;
        }
        const d = new Date(val);
        if (isNaN(d.valueOf())) {
            return invalidValue(l("Date"), val, key, parent);
        }
        return d;
    }

    // Then modify the transformObject function inside the transform function
    function transformObject(props: { [k: string]: any }, additional: any, val: any): any {
        if (val === null || typeof val !== "object" || Array.isArray(val)) {
            return invalidValue(l(ref || "object"), val, key, parent);
        }
        const result: any = {};
        Object.getOwnPropertyNames(props).forEach(key => {
            const prop = props[key];
            const v = Object.prototype.hasOwnProperty.call(val, key) ? val[key] : undefined;

            // Special handling for executionType
            if (key === "executionType" && (v === undefined || v === null)) {
                result[prop.key] = "";
            } else if (key === "priceType" && (v === undefined || v === null)) {
                result[prop.key] = "fixed";
            } else if (key === "min" && (v === undefined || v === null)) {
                result[prop.key] = 0;
            } else if (key === "max" && (v === undefined || v === null)) {
                result[prop.key] = 0;
            } else if (key === "value" && (v === undefined || v === null)) {
                result[prop.key] = 0;
            }else if (key === "currency" && (v === undefined || v === null)) {
                result[prop.key] = "€";
            }else if (key === "supplierFileId" && (v === undefined || v === null)) {
                result[prop.key] = "";
            } else {
                result[prop.key] = transform(v, prop.typ, getProps, key, ref);
            }
        });
        Object.getOwnPropertyNames(val).forEach(key => {
            if (!Object.prototype.hasOwnProperty.call(props, key)) {
                result[key] = transform(val[key], additional, getProps, key, ref);
            }
        });
        return result;
    }

    if (typ === "any") return val;
    if (typ === null) {
        if (val === null) return val;
        return invalidValue(typ, val, key, parent);
    }
    if (typ === false) return invalidValue(typ, val, key, parent);
    let ref: any = undefined;
    while (typeof typ === "object" && typ.ref !== undefined) {
        ref = typ.ref;
        typ = typeMap[typ.ref];
    }
    if (Array.isArray(typ)) return transformEnum(typ, val);
    if (typeof typ === "object") {
        return typ.hasOwnProperty("unionMembers") ? transformUnion(typ.unionMembers, val)
            : typ.hasOwnProperty("arrayItems") ? transformArray(typ.arrayItems, val)
                : typ.hasOwnProperty("props") ? transformObject(getProps(typ), typ.additional, val)
                    : invalidValue(typ, val, key, parent);
    }
    // Numbers can be parsed by Date but shouldn't be.
    if (typ === Date && typeof val !== "number") return transformDate(val);
    return transformPrimitive(typ, val);
}

function cast<T>(val: any, typ: any): T {
    return transform(val, typ, jsonToJSProps);
}

function uncast<T>(val: T, typ: any): any {
    return transform(val, typ, jsToJSONProps);
}

function l(typ: any) {
    return { literal: typ };
}

function a(typ: any) {
    return { arrayItems: typ };
}

function u(...typs: any[]) {
    return { unionMembers: typs };
}

function o(props: any[], additional: any) {
    return { props, additional };
}

function m(additional: any) {
    return { props: [], additional };
}

function r(name: string) {
    return { ref: name };
}

const typeMap: any = {
    "ProductAndSupplierMapModel": o([
        { json: "status", js: "status", typ: "" },
        { json: "data", js: "data", typ: r("Data") },
    ], false),
    "Data": o([
        { json: "productDenomination", js: "productDenomination", typ: a(r("ProductDenomination")) },
        { json: "supplierDetails", js: "supplierDetails", typ: a(r("SupplierDetail")) },
    ], false),
    "ProductDenomination": o([
        { json: "_id", js: "_id", typ: u(undefined, "") },
        { json: "productId", js: "productId", typ: u(undefined, "") },
        { json: "productsku", js: "productsku", typ: u(undefined, "") },
        { json: "productDenominationId", js: "productDenominationId", typ: u(undefined, "") },
        { json: "productDenominationsku", js: "productDenominationsku", typ: u(undefined, "") },
        { json: "supplierId", js: "supplierId", typ: u(undefined, "") },
        { json: "subSupplierId", js: "subSupplierId", typ: u(undefined, "") },
        { json: "subSupplierName", js: "subSupplierName", typ: u(undefined, "") },
        { json: "supplierName", js: "supplierName", typ: u(undefined, "") },
        { json: "supplierCategory", js: "supplierCategory", typ: u(undefined, "") },
        { json: "executionType", js: "executionType", typ: u(undefined, "") },
        { json: "supplierProviderName", js: "supplierProviderName", typ: u(undefined, "") },
        { json: "supplierProviderCode", js: "supplierProviderCode", typ: u(undefined, "") },
        { json: "supplerProductDenominationsku", js: "supplerProductDenominationsku", typ: u(undefined, "") },
        { json: "supplierDiscountType", js: "supplierDiscountType", typ: u(undefined, "") },
        { json: "supplierDiscount", js: "supplierDiscount", typ: u(undefined, 0) },
        { json: "cashbackType", js: "cashbackType", typ: u(undefined, "") },
        { json: "supplierFileId", js: "supplierFileId", typ: u(undefined, "") },
        { json: "cashback", js: "cashback", typ: u(undefined, 0) },
        { json: "quantity", js: "quantity", typ: u(undefined, 0) },
        { json: "markupPrice", js: "markupPrice", typ: u(undefined, 0) },
        { json: "cost_price_at_sale", js: "cost_price_at_sale", typ: u(undefined, 0) },
        { json: "excutionLevel", js: "excutionLevel", typ: u(undefined, 0) },
        { json: "isCustomMapping", js: "isCustomMapping", typ: true },
        { json: "isActive", js: "isActive", typ: true },
        { json: "isDelete", js: "isDelete", typ: true },
        { json: "createdAt", js: "createdAt", typ: Date },
        { json: "updatedAt", js: "updatedAt", typ: Date },
        { json: "denomination", js: "denomination", typ: u(undefined, 0) },
        { json: "__v", js: "__v", typ: u(undefined, 0) },
        // Set by the nightly sync when a supplier stops listing the SKU. The
        // cast rejects any field it does not know (see `typ === false` in
        // transform), so a new field on the API model must be declared here or
        // the entire mapping screen renders empty.
        { json: "deactivatedBySync", js: "deactivatedBySync", typ: u(undefined, true) },
        // Audit fields. They exist on the API model and are written by any code
        // path that stamps who changed a mapping, so they can appear on some
        // documents and not others. Undeclared, one stamped document empties the
        // whole screen — which is exactly what happened on 30 Aug after a
        // cleanup script set updatedBy on a single row.
        { json: "createdBy", js: "createdBy", typ: u(undefined, "") },
        { json: "updatedBy", js: "updatedBy", typ: u(undefined, "") },
        // FIXED: Changed the reference to match the interface name
        { json: "supplerProductDenominationAmt", js: "supplerProductDenominationAmt", typ: u(undefined, r("SupplerProductDenominationAmtModel")) },
    ], false),

    // FIXED: Changed the key name to match what's referenced above
    "SupplerProductDenominationAmtModel": o([
        { json: "value", js: "value", typ: 0 },
        { json: "currency", js: "currency", typ: "" },
        { json: "priceType", js: "priceType", typ: u("fixed", "range") },
        { json: "min", js: "min", typ: u(undefined, 0) },
        { json: "max", js: "max", typ: u(undefined, 0) },
    ], false),
    "SupplierDetail": o([
        { json: "supplierName", js: "supplierName", typ: u(undefined, "") },
        { json: "supplierCategory", js: "supplierCategory", typ: u(undefined, a("")) },
        { json: "supplierProductDenominationsku", js: "supplierProductDenominationsku", typ: u(undefined, null, "") },
        { json: "supplierId", js: "supplierId", typ: u(undefined, "") },
    ], false),
};